import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { api, ApiError, type Profile } from "@/api/client";

type User = { id: string; email: string };

type Ctx = {
  user: User | null;
  profile: Profile | null;
  isAdmin: boolean;
  isStaff: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthCtx = createContext<Ctx>({} as Ctx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.auth.me();
      setUser({ id: me.user.id, email: me.user.email });
      setProfile(me.profile);
      setIsAdmin(me.user.roles.includes("admin"));
      setIsStaff(me.user.roles.includes("admin") || me.user.roles.includes("staff"));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
        setIsStaff(false);
      } else {
        // Network or server hiccup — keep last-known state, don't silently log out
        console.warn("[auth] refresh failed:", err);
      }
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  return (
    <AuthCtx.Provider
      value={{
        user,
        profile,
        isAdmin,
        isStaff,
        loading,
        signOut: async () => {
          await api.auth.logout().catch(() => {});
          setUser(null);
          setProfile(null);
          setIsAdmin(false);
          setIsStaff(false);
        },
        refreshProfile: refresh,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
